import { Flex } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { useParallelQueries } from "@/hooks/useHttp"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { AiInsight, Package, Runtime } from "@/icons"
import { StatisticWidget } from "@/molecules/Widget"

import styles from "./SbomTab.module.scss"

export const HeaderSbomTab = () => {
  const { t } = useTranslation()
  const convertors = useNumberConvertor()
  const { ROOT, SBOM } = NAMES_ROUTES.DISCOVERY
  const root = `/${ROOT}/${SBOM}`
  const isPackageRuntime = "isPackageRuntime"
  const isPackageAiRelated = "isPackageAiRelated"
  const valueFilter = "YES"
  const { PACKAGE } = RESOURCES_NAMES.INVENTORIES
  const params = { pageNumber: 0, pageSize: 1 }
  const runtimeParams = { [isPackageRuntime]: valueFilter }
  const aiRelatedParams = { [isPackageAiRelated]: valueFilter }
  const runtimeAiRelatedParams = { ...runtimeParams, ...aiRelatedParams }
  const urls = [
    { url: PACKAGE, params },
    { url: PACKAGE, params: { ...runtimeParams, ...params } },
    { url: PACKAGE, params: { ...aiRelatedParams, ...params } },
    { url: PACKAGE, params: { ...runtimeAiRelatedParams, ...params } }
  ]

  const context = useParallelQueries<KosmosApi.Response<unknown>>(urls)

  const getParams = (params: Record<string, string>) => {
    const searchParams = new URLSearchParams(params)

    return `?${searchParams.toString()}`
  }

  const getTotal = (
    context: ReturnType<typeof useParallelQueries<KosmosApi.Response<unknown>>>,
    index: number
  ) => context?.[0]?.[index]?.data?.metadata?.total || 0

  const getPercentage = (value: number, total: number) => {
    if (!total || !value) return 0

    return Number(((value / total) * 100).toFixed(2))
  }

  const isLoading = context?.[1]?.isPending
  const total = getTotal(context, 0)
  const runtimeTotal = getTotal(context, 1)
  const aiRelatedTotal = getTotal(context, 2)
  const runtimeAiRelatedTotal = getTotal(context, 3)

  return (
    <Flex gap={8}>
      <StatisticWidget
        border
        flex
        loading={isLoading}
        to={root}
        title={
          <Flex gap={4} align="center" className={styles.title}>
            <Package className={styles.icon} />
            {`${t("packages.names.all")} (${t("packages.shortNames.all")})`}
          </Flex>
        }
        value={convertors.abbreviateNumber(total)}
        percentage={100}
      />
      <StatisticWidget
        border
        flex
        loading={isLoading}
        to={`${root}${getParams(runtimeParams)}`}
        title={
          <Flex gap={4} align="center" className={styles.title}>
            <Runtime className={styles.icon} />
            {`${t("packages.names.runtime")} (${t("packages.shortNames.runtime")})`}
          </Flex>
        }
        value={convertors.abbreviateNumber(runtimeTotal)}
        percentage={getPercentage(runtimeTotal, total)}
      />
      <StatisticWidget
        border
        flex
        loading={isLoading}
        to={`${root}${getParams(aiRelatedParams)}`}
        title={
          <Flex gap={4} align="center" className={styles.title}>
            <AiInsight className={styles.icon} />
            {`${t("packages.names.aiBom")} (${t("packages.shortNames.aiBom")})`}
          </Flex>
        }
        value={convertors.abbreviateNumber(aiRelatedTotal)}
        percentage={getPercentage(aiRelatedTotal, total)}
      />
      <StatisticWidget
        border
        flex
        loading={isLoading}
        to={`${root}${getParams(runtimeAiRelatedParams)}`}
        title={
          <Flex gap={4} align="center" className={styles.title}>
            <Runtime className={styles.icon} />
            <AiInsight className={styles.icon} />
            {`${t("packages.names.aiRuntimeBom")} (${t("packages.shortNames.aiRuntimeBom")})`}
          </Flex>
        }
        value={convertors.abbreviateNumber(runtimeAiRelatedTotal)}
        percentage={getPercentage(runtimeAiRelatedTotal, total)}
      />
    </Flex>
  )
}
